import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  Button, Form, Input, InputNumber, Modal, Select, Space, Typography,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import DatePicker from 'components/DatePicker/DatePicker';
import { useCreateEvent, useUpdateEvent } from '../hooks';
import * as fmt from '../format';
import { lineLitres, skuCatalog } from '../events';

const { Text } = Typography;

const toFormValues = (initial) => ({
  name: initial?.name || '',
  dates: initial?.startDate ? [dayjs(initial.startDate), dayjs(initial.endDate)] : null,
  notes: initial?.notes || '',
  lines: initial?.lines?.length ? initial.lines : [{ unit: 'litres' }],
});

const fromFormValues = (values) => ({
  name: values.name,
  startDate: values.dates[0].format('YYYY-MM-DD'),
  endDate: values.dates[1].format('YYYY-MM-DD'),
  notes: values.notes || null,
  lines: (values.lines || [])
    .filter((line) => line && line.sku)
    .map((line) => ({
      sku: line.sku,
      quantity: line.quantity,
      unit: line.unit,
    })),
});

const EventModal = ({
  open, initial, plan, onClose,
}) => {
  const [form] = Form.useForm();
  const createEvent = useCreateEvent();
  const updateEvent = useUpdateEvent();
  const editing = !!initial?.id;
  const catalog = useMemo(() => skuCatalog(plan), [plan]);
  const lines = Form.useWatch('lines', form);

  useEffect(() => {
    if (open) form.setFieldsValue(toFormValues(initial));
  }, [open, initial, form]);

  const skuOptions = useMemo(() => {
    const groups = [];
    catalog.forEach((sku) => {
      let group = groups.find((g) => g.label === sku.beerName);
      if (!group) {
        group = { label: sku.beerName, options: [] };
        groups.push(group);
      }
      const name = sku.productName && sku.productName !== sku.sku ? ` · ${sku.productName}` : '';
      group.options.push({ value: sku.sku, label: `${sku.sku}${name}` });
    });
    return groups;
  }, [catalog]);

  const total = (lines || []).reduce((sum, line) => sum + (lineLitres(line || {}, catalog) || 0), 0);
  const complete = (lines || []).some((line) => line && line.sku && line.quantity);

  const submit = async () => {
    const values = await form.validateFields();
    const event = fromFormValues(values);
    if (!event.lines.length) return;
    if (editing) await updateEvent.mutateAsync({ id: initial.id, ...event });
    else await createEvent.mutateAsync(event);
    onClose();
  };

  return (
    <Modal
      title={editing ? `Edit ${initial.name}` : 'Add demand event'}
      visible={open}
      onCancel={onClose}
      onOk={submit}
      okText={editing ? 'Save' : 'Add event'}
      confirmLoading={createEvent.isLoading || updateEvent.isLoading}
      width={720}
      destroyOnClose
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name' }]}>
          <Input placeholder="e.g. China order, Beervana, lost Woolworths ranging" />
        </Form.Item>
        <Form.Item
          name="dates"
          label="Dates"
          extra="Same start and end date for an order due that week. Litres are spread evenly across the range."
          rules={[{ required: true, message: 'Dates' }]}
        >
          <DatePicker.RangePicker format="D MMM YYYY" />
        </Form.Item>
        <Form.Item
          label="SKUs"
          extra={complete ? (
            <Text type="secondary">{`${fmt.litres(total)} added to demand across these dates`}</Text>
          ) : 'Positive adds demand. Negative removes it.'}
        >
          <Form.List name="lines">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Space key={field.key} align="baseline">
                    <Form.Item
                      {...field}
                      name={[field.name, 'sku']}
                      rules={[{ required: true, message: 'SKU' }]}
                    >
                      <Select
                        showSearch
                        optionFilterProp="label"
                        style={{ width: 280 }}
                        options={skuOptions}
                        placeholder="SKU"
                      />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, 'quantity']}
                      rules={[{ required: true, message: 'Quantity' }]}
                    >
                      <InputNumber placeholder="Qty" style={{ width: 120 }} />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, 'unit']}
                      rules={[{ required: true, message: 'Unit' }]}
                    >
                      <Select
                        style={{ width: 110 }}
                        options={[
                          { value: 'litres', label: 'Litres' },
                          { value: 'units', label: 'Units' },
                        ]}
                      />
                    </Form.Item>
                    {fields.length > 1 && (
                      <MinusCircleOutlined onClick={() => remove(field.name)} />
                    )}
                  </Space>
                ))}
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ unit: 'litres' })}>
                  Add SKU
                </Button>
              </>
            )}
          </Form.List>
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

EventModal.propTypes = {
  open: PropTypes.bool.isRequired,
  initial: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    lines: PropTypes.arrayOf(PropTypes.object),
  }),
  plan: PropTypes.shape({
    beers: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

EventModal.defaultProps = { initial: null };

export default EventModal;
